import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Product } from '@runninglime/shared';
import { apiGet } from '../../lib/api.ts';
import { useCart } from '../../context/CartContext.tsx';
import { formatPrice, categoryLabel } from '../../lib/utils.ts';
import { ProductPlaceholder, LoadingSpinner } from '../../components/ui/Shared.tsx';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [adding, setAdding] = useState(false);
  const [addedMsg, setAddedMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      const res = await apiGet<Product>(`/products/${id}`);
      if (res.success && res.data) {
        setProduct(res.data);
        setQuantity(res.data.variants[0]?.minOrderQty || 1);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  useEffect(() => {
    if (product) {
      setQuantity(product.variants[selectedVariant]?.minOrderQty || 1);
    }
  }, [selectedVariant, product]);

  if (loading) return <LoadingSpinner />;
  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-dark">Product not found</h2>
        <button onClick={() => navigate('/store')} className="mt-4 text-green font-medium hover:underline">
          Back to Store
        </button>
      </div>
    );
  }

  const variant = product.variants[selectedVariant];

  // Calculate effective price
  let effectivePrice = variant.pricePerUnit;
  if (variant.bulkPricing) {
    for (const tier of [...variant.bulkPricing].sort((a, b) => b.minQty - a.minQty)) {
      if (quantity >= tier.minQty) {
        effectivePrice = tier.pricePerUnit;
        break;
      }
    }
  }

  const handleAdd = async () => {
    setAdding(true);
    const result = await addItem(product.id, selectedVariant, quantity);
    if (result.success) {
      setAddedMsg('Added to cart!');
      setTimeout(() => setAddedMsg(''), 2000);
    } else {
      setAddedMsg(result.error || 'Failed to add');
    }
    setAdding(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
        <button onClick={() => navigate('/store')} className="hover:text-green transition-colors">Store</button>
        <span>/</span>
        <span className="text-dark font-medium">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Image — prefer variant image, fall back to main product image */}
        <div className="img-zoom rounded-2xl">
          <ProductPlaceholder name={product.name} category={product.category} imageUrl={variant.imageUrl || product.imageUrl} />
        </div>

        {/* Details */}
        <div className="glass rounded-2xl p-8 animate-slide-in-right">
          <span className="text-sm font-medium text-green bg-green/10 px-3 py-1 rounded-full">
            {categoryLabel(product.category)}
          </span>

          <h1 className="text-3xl font-bold text-dark mt-4 mb-3">{product.name}</h1>
          <p className="text-gray-500 leading-relaxed mb-6">{product.description}</p>

          {/* Variant Selector */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-dark mb-3">Size & Format</h3>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedVariant(i)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    selectedVariant === i
                      ? 'bg-lime text-dark shadow-md ring-2 ring-lime/50'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-lime'
                  }`}
                >
                  {v.size} {v.unit}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Info */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-2xl font-bold text-dark">{formatPrice(effectivePrice)}</span>
              <span className="text-gray-400">/unit</span>
              {effectivePrice < variant.pricePerUnit && (
                <span className="text-sm text-gray-400 line-through ml-2">
                  {formatPrice(variant.pricePerUnit)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">Min. order: {variant.minOrderQty} units</p>

            {variant.bulkPricing && variant.bulkPricing.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-600 mb-1">Bulk discounts:</p>
                {variant.bulkPricing
                  .sort((a, b) => a.minQty - b.minQty)
                  .map((tier, i) => (
                    <p key={i} className={`text-xs ${quantity >= tier.minQty ? 'text-green font-medium' : 'text-gray-500'}`}>
                      {tier.minQty}+ units: {formatPrice(tier.pricePerUnit)}/unit
                      {quantity >= tier.minQty && ' ✓'}
                    </p>
                  ))}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-dark mb-3">Quantity</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity(Math.max(variant.minOrderQty, quantity - variant.minOrderQty))}
                className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-lg font-bold hover:bg-lime/20 transition-colors"
              >
                −
              </button>
              <input
                type="number"
                min={variant.minOrderQty}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(variant.minOrderQty, parseInt(e.target.value) || variant.minOrderQty))}
                className="w-24 text-center py-2 rounded-xl border border-gray-200 focus:border-lime outline-none"
              />
              <button
                onClick={() => setQuantity(quantity + variant.minOrderQty)}
                className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-lg font-bold hover:bg-lime/20 transition-colors"
              >
                +
              </button>
            </div>
          </div>

          {/* Subtotal */}
          <div className="flex items-center justify-between bg-dark/5 rounded-xl p-4 mb-6 transition-all duration-300">
            <span className="text-sm font-medium text-gray-600">Subtotal</span>
            <span className="text-xl font-bold text-dark transition-all duration-200">
              {formatPrice(effectivePrice * quantity)}
            </span>
          </div>

          {/* Add to Cart */}
          <button
            onClick={handleAdd}
            disabled={adding}
            className="w-full btn-gradient py-4 rounded-xl text-lg font-semibold disabled:opacity-60 active:scale-[0.98] transition-transform"
          >
            {adding ? 'Adding...' : 'Add to Cart'}
          </button>

          {addedMsg && (
            <p className={`text-center mt-3 text-sm font-medium animate-success-pop ${addedMsg.includes('Failed') ? 'text-red-600' : 'text-green'}`}>
              {addedMsg}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
